// `defineModel` — the one function every model file calls. It accepts a small,
// forgiving input (a single `parts()` for the common one-variant case, or an
// explicit `variants` list), validates it with *teaching* errors, fills
// defaults, and returns a fully-formed {@link Model}. Authoring everything
// through this function is what keeps the model set from drifting: a malformed
// model fails loudly at load time (and in the registry test) instead of
// silently rendering wrong or vanishing from the gallery.

import type { Animation, Interaction, Keyframe, Model, Part, Socket, Station, TileFootprint, Track, Variant, Vec3 } from "./types";
import { animatedTargets } from "./animate";
import { ACTOR_RIG, isRigPart } from "./rig";

/** What an author passes to {@link defineModel}. Provide exactly one of
 *  `parts` (sugar for a single "default" variant) or `variants`. */
export interface ModelInput {
  id: string;
  name: string;
  tags: string[];
  summary?: string;
  description?: string;
  author?: string;
  /** Id of the {@link Batch} (production run) this model belongs to — its
   *  provenance. Validated kebab-case here; resolved against `src/batches`
   *  by the registry guardrail and the consumers. */
  batchId?: string;
  footprint?: number;
  /** Grid footprint in whole tiles — `{ w, h }`, the integer game footprint the
   *  viewer seats the model onto. Validated here (positive integers). */
  tiles?: TileFootprint;
  /** Which model-local axis the authored front points along (default "+z").
   *  See {@link Model.front}; consumers auto-correct facing from it. */
  front?: "+z" | "-z" | "+x" | "-x";
  /** Internal uniform scale baked into the model's geometry and anchors at
   *  definition time — a real-world size correction, not a viewer control. See
   *  {@link Model.scale}. Must be a positive number; defaults to 1. */
  scale?: number;
  /** Sugar: the parts of a single "default" variant. */
  parts?: () => Part[];
  /** Explicit variants (two or more states/situations). */
  variants?: Variant[];
  /** Id of the variant to show first / thumbnail by, when it should not be the
   *  first one listed (e.g. stages listed foundation-first but defaulting to
   *  `"complete"`). Must match a variant id; defaults to the first variant. */
  defaultVariant?: string;
  /** Optional animations over the variants. */
  animations?: Animation[];
  /** Optional anchor points where an actor engages this model. */
  stations?: Station[];
  /** Optional interaction animations (an actor using this model at a station). */
  interactions?: Interaction[];
  /** Optional anchor points where the consumer mounts external content. */
  sockets?: Socket[];
  /** Dynamic layout callback: given the fills, return per-socket overrides for
   *  position/rotation/scale. See {@link Model.socketLayout}. */
  socketLayout?: Model["socketLayout"];
  /** Which slot type ids this model fits into — the slot-type counterpart of a
   *  tag like `"carriable"`. A socket with `slotType: "carry"` offers every
   *  model whose `fits` includes `"carry"`. */
  fits?: string[];
  /** Whether this model is in sync with its counterpart in the source repo
   *  (age-of-mcp). Set `false` when you edit it here so it reads as diverged;
   *  back to `true` once synced. `false` lights up an "out of sync" indicator in
   *  the detail panel; omit it for the untracked baseline. See {@link Model.synced}. */
  synced?: boolean;
}

const KEBAB = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function fail(id: string, what: string, fix: string): never {
  throw new Error(`Invalid model "${id}": ${what}. ${fix}`);
}

/** Prettify a kebab id into a default label ("tree-oak" → "Tree oak"). */
export function prettify(id: string): string {
  const s = id.replace(/[-_]/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Uniform model scale (baked at definition time) ───────────────────────────
// A model's `scale` is a uniform size correction applied about the model origin.
// It is baked into every coordinate the model publishes so that downstream
// consumers (which all read `variant.parts()` and the model's anchors) see an
// already-scaled model and never re-apply it. A uniform scale about the origin
// is exactly `position × s` and `scale × s` on each part, so no geometry needs
// cloning here — only transforms are multiplied.

/** Multiply an optional Vec3 by a uniform factor (fresh tuple, or undefined). */
function scaleVec(v: Vec3 | undefined, s: number): Vec3 | undefined {
  return v ? [v[0] * s, v[1] * s, v[2] * s] : v;
}

/** Multiply a Part's own scale (number | Vec3 | undefined) by a uniform factor. */
function scalePartScale(ps: number | Vec3 | undefined, s: number): number | Vec3 {
  if (ps === undefined) return s;
  if (typeof ps === "number") return ps * s;
  return [ps[0] * s, ps[1] * s, ps[2] * s];
}

/** Uniformly scale a part list about the model origin (bakes the factor into
 *  each part's position and scale). */
function scaleParts(parts: Part[], s: number): Part[] {
  return parts.map((p) => {
    const [px, py, pz] = p.position ?? [0, 0, 0];
    return { ...p, position: [px * s, py * s, pz * s] as Vec3, scale: scalePartScale(p.scale, s) };
  });
}

/** Scale a track's spatial fields: its pivot and translation keyframes are
 *  distances (scaled); rotation and scale keyframes are unitless (left alone). */
function scaleTrack(tr: Track, s: number): Track {
  return {
    ...tr,
    ...(tr.pivot ? { pivot: scaleVec(tr.pivot, s)! } : {}),
    ...(tr.position
      ? { position: tr.position.map((k): Keyframe<Vec3> => ({ ...k, value: scaleVec(k.value, s)! })) }
      : {}),
  };
}

/**
 * Validate and normalise a model. Throws a teaching error (what is wrong + how
 * to fix it) on any violation, so problems surface at the seam, never deep in
 * the renderer.
 */
export function defineModel(input: ModelInput): Model {
  const { id } = input;
  if (!id || !KEBAB.test(id)) {
    fail(id || "(missing)", "id must be non-empty kebab-case", 'Use lowercase words joined by "-", e.g. "watch-tower".');
  }
  if (!input.name?.trim()) fail(id, "name is required", "Add a human display name.");
  if (!Array.isArray(input.tags) || input.tags.length === 0) {
    fail(id, "at least one tag is required", 'Add tags like ["building", "medieval"] so search can find it.');
  }
  for (const t of input.tags) {
    if (t !== t.toLowerCase()) fail(id, `tag "${t}" must be lowercase`, "Lowercase every tag for consistent search.");
  }
  if (input.batchId !== undefined && !KEBAB.test(input.batchId)) {
    fail(id, `batchId "${input.batchId}" must be kebab-case`, "Point it at a batch id from src/batches, e.g. \"fable\".");
  }
  if (input.tiles !== undefined) {
    const { w, h } = input.tiles;
    if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1) {
      fail(id, `tiles must be whole tile counts ≥ 1, got { w: ${w}, h: ${h} }`,
        "Set the model's grid footprint in tiles, e.g. tiles: { w: 2, h: 2 } for a 2×2 building.");
    }
  }
  if (input.scale !== undefined && (!Number.isFinite(input.scale) || input.scale <= 0)) {
    fail(id, `scale must be a positive number, got ${input.scale}`,
      "Use a uniform size multiplier like 1.6 (omit it for the default of 1).");
  }
  if (input.front !== undefined && !["+z", "-z", "+x", "-x"].includes(input.front)) {
    fail(id, `front must be "+z", "-z", "+x" or "-x", got ${JSON.stringify(input.front)}`,
      "Name the model-local axis the authored front points along (omit it for the default \"+z\").");
  }
  if (input.synced !== undefined && typeof input.synced !== "boolean") {
    fail(id, `synced must be true or false, got ${input.synced}`,
      "Set synced: false when the model has diverged from age-of-mcp, true once synced (or omit it).");
  }
  const scale = input.scale ?? 1;

  const hasParts = typeof input.parts === "function";
  const hasVariants = Array.isArray(input.variants) && input.variants.length > 0;
  if (hasParts === hasVariants) {
    fail(id, "provide exactly one of `parts` or `variants`",
      "Use `parts()` for a single-look model, or `variants: [...]` for multiple states.");
  }

  const variants: Variant[] = hasParts
    ? [{ id: "default", label: "Default", parts: input.parts! }]
    : input.variants!;

  // Variant ids must be unique and kebab-case; each must yield ≥1 part.
  const seen = new Set<string>();
  for (const v of variants) {
    if (!v.id || !KEBAB.test(v.id)) fail(id, `variant id "${v.id}" must be kebab-case`, "e.g. \"stage-0\", \"winter\".");
    if (seen.has(v.id)) fail(id, `duplicate variant id "${v.id}"`, "Variant ids must be unique within a model.");
    seen.add(v.id);
    if (typeof v.parts !== "function") fail(id, `variant "${v.id}" needs a parts() function`, "Return the variant's Part[].");
  }

  // The default variant is decoupled from order: a model may list its variants
  // in a natural progression (a building's foundation → complete) yet present a
  // later one by default. A misnamed default would silently fall back, so it is
  // validated here against the known variant ids.
  let defaultVariantId = variants[0]!.id;
  if (input.defaultVariant !== undefined) {
    if (!seen.has(input.defaultVariant)) {
      fail(id, `defaultVariant "${input.defaultVariant}" is not a variant id`,
        `Point it at one of: ${[...seen].join(", ")}.`);
    }
    defaultVariantId = input.defaultVariant;
  }

  const animations = input.animations ?? [];
  const animIds = new Set<string>();
  for (const a of animations) {
    if (!a.id || !KEBAB.test(a.id)) fail(id, `animation id "${a.id}" must be kebab-case`, 'e.g. "spin", "open".');
    if (animIds.has(a.id)) fail(id, `duplicate animation id "${a.id}"`, "Animation ids must be unique within a model.");
    animIds.add(a.id);
    if (!(a.duration > 0)) fail(id, `animation "${a.id}" needs a positive duration`, "Set duration in seconds.");
    if (!a.tracks?.length) fail(id, `animation "${a.id}" has no tracks`, "Add at least one track targeting a named part.");
    for (const v of a.variants ?? []) {
      if (!seen.has(v)) fail(id, `animation "${a.id}" restricts to unknown variant "${v}"`, `Known variants: ${[...seen].join(", ")}.`);
    }
  }

  // The named parts across every variant — the legal targets for a self track
  // (an animation track, or an interaction's `subject: "self"` track).
  const partNames = new Set<string>();
  for (const v of variants) for (const p of v.parts()) if (p.name) partNames.add(p.name);

  // Every animated target must be a part name in at least one variant — a
  // misspelled target would otherwise animate nothing, silently.
  for (const t of animatedTargets(animations)) {
    if (!partNames.has(t)) {
      fail(id, `animation targets part "${t}" that no variant defines`,
        `Name a part \`name: "${t}"\` in a variant, or fix the track's target. Named parts: ${[...partNames].join(", ") || "(none)"}.`);
    }
  }

  // Stations — the anchors interactions place an actor at. Validated up front so
  // an interaction can reference them by id below.
  const stations = input.stations ?? [];
  const stationIds = new Set<string>();
  for (const s of stations) {
    if (!s.id || !KEBAB.test(s.id)) fail(id, `station id "${s.id}" must be kebab-case`, 'e.g. "front", "bench-left".');
    if (stationIds.has(s.id)) fail(id, `duplicate station id "${s.id}"`, "Station ids must be unique within a model.");
    stationIds.add(s.id);
    if (!Array.isArray(s.position) || s.position.length !== 3) {
      fail(id, `station "${s.id}" needs a [x, y, z] position`, "Give the model-local spot the actor stands at.");
    }
  }

  // Interactions — two-body animations of an actor (and optionally the model)
  // at a station. Self tracks must name a real part; actor tracks must name a
  // rig part; the station must exist.
  const interactions = input.interactions ?? [];
  const interactionIds = new Set<string>();
  for (const it of interactions) {
    if (!it.id || !KEBAB.test(it.id)) fail(id, `interaction id "${it.id}" must be kebab-case`, 'e.g. "draw-water", "saw".');
    if (interactionIds.has(it.id)) fail(id, `duplicate interaction id "${it.id}"`, "Interaction ids must be unique within a model.");
    interactionIds.add(it.id);
    if (!(it.duration > 0)) fail(id, `interaction "${it.id}" needs a positive duration`, "Set duration in seconds.");
    if (!it.tracks?.length) fail(id, `interaction "${it.id}" has no tracks`, "Add at least one track (subject 'self' or 'actor').");
    if (!stationIds.has(it.station)) {
      fail(id, `interaction "${it.id}" references unknown station "${it.station}"`,
        `Add it to \`stations\`. Known stations: ${[...stationIds].join(", ") || "(none)"}.`);
    }
    for (const v of it.variants ?? []) {
      if (!seen.has(v)) fail(id, `interaction "${it.id}" restricts to unknown variant "${v}"`, `Known variants: ${[...seen].join(", ")}.`);
    }
    for (const tr of it.tracks ?? []) {
      if ((tr.subject ?? "self") === "actor") {
        if (!isRigPart(tr.target)) {
          fail(id, `interaction "${it.id}" actor track targets "${tr.target}", not an actor rig part`,
            `Use one of: ${ACTOR_RIG.join(", ")}.`);
        }
      } else if (!partNames.has(tr.target)) {
        fail(id, `interaction "${it.id}" self track targets part "${tr.target}" that no variant defines`,
          `Name a part \`name: "${tr.target}"\` in a variant, or set \`subject: "actor"\`. Named parts: ${[...partNames].join(", ") || "(none)"}.`);
      }
    }
  }

  // Sockets — anchors where the consumer mounts external content (a carried
  // good, a flag). Pure metadata the model publishes; the content is supplied at
  // bake time, so nothing is validated against it here beyond the anchor itself.
  const sockets = input.sockets ?? [];
  const socketIds = new Set<string>();
  for (const s of sockets) {
    if (!s.id || !KEBAB.test(s.id)) fail(id, `socket id "${s.id}" must be kebab-case`, 'e.g. "carry", "flag".');
    if (socketIds.has(s.id)) fail(id, `duplicate socket id "${s.id}"`, "Socket ids must be unique within a model.");
    socketIds.add(s.id);
    if (!Array.isArray(s.position) || s.position.length !== 3) {
      fail(id, `socket "${s.id}" needs a [x, y, z] position`, "Give the model-local point the mounted content sits at.");
    }
    if (s.slotType !== undefined && !KEBAB.test(s.slotType)) {
      fail(id, `socket "${s.id}" slotType "${s.slotType}" must be kebab-case`,
        'Point it at a slot type id from src/slot-types, e.g. "carry".');
    }
    for (const a of s.accepts ?? []) {
      if (a !== a.toLowerCase()) {
        fail(id, `socket "${s.id}" accepts tag "${a}" that is not lowercase`,
          "Lowercase every accepted tag so it matches the content models' tags.");
      }
    }
  }

  const fits = input.fits ?? [];
  for (const f of fits) {
    if (!KEBAB.test(f)) {
      fail(id, `fits entry "${f}" must be kebab-case`,
        'Point it at a slot type id from src/slot-types, e.g. "carry".');
    }
  }

  // Bake the uniform scale into every coordinate the model publishes, so the
  // whole set (geometry + anchors) grows together and no consumer re-applies it.
  // scale === 1 is the overwhelmingly common case and a pure no-op, so the
  // unscaled branch keeps existing models byte-identical.
  const scaled = scale !== 1;
  const outVariants = variants.map((v) => ({
    ...v,
    label: v.label ?? prettify(v.id),
    parts: scaled ? () => scaleParts(v.parts(), scale) : v.parts,
  }));
  const outFootprint = input.footprint !== undefined && scaled ? input.footprint * scale : input.footprint;
  const outAnimations = scaled
    ? animations.map((a) => ({ ...a, tracks: a.tracks.map((t) => scaleTrack(t, scale)) }))
    : animations;
  const outStations = scaled ? stations.map((s) => ({ ...s, position: scaleVec(s.position, scale)! })) : stations;
  const outInteractions = scaled
    ? interactions.map((it) => ({ ...it, tracks: it.tracks.map((t) => scaleTrack(t, scale)) }))
    : interactions;
  const outSockets = scaled
    ? sockets.map((s) => ({ ...s, position: scaleVec(s.position, scale)!, scale: (s.scale ?? 1) * scale }))
    : sockets;

  return {
    id,
    name: input.name.trim(),
    tags: input.tags.map((t) => t.trim()),
    summary: input.summary?.trim(),
    description: input.description?.trim(),
    author: input.author?.trim(),
    batchId: input.batchId,
    footprint: outFootprint,
    tiles: input.tiles,
    front: input.front,
    scale,
    variants: outVariants,
    defaultVariantId,
    animations: outAnimations,
    stations: outStations,
    interactions: outInteractions,
    sockets: outSockets,
    socketLayout: input.socketLayout,
    fits,
    synced: input.synced,
  };
}
